import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faNoteSticky,
  faPlus,
  faMinus,
  faMessage,
  faWheelchair,
} from "@fortawesome/free-solid-svg-icons";
import DropDownBabyChairs from "./inputs/DropDownBabyChairs";
import styled from "styled-components";
import PhoneField from "./PhoneField";

import { BabyChair } from "../icons/SvgIcons";

const FIELD_HEIGHT = 44;

const MAIN_COLOR_FALLBACK =
  "var(--step-three-main-color, var(--widget-main-color, #ee754c))";

const getMainColor = (props) => props.$mainColor || MAIN_COLOR_FALLBACK;

const getAccentOrWhite = (active, mainColor) =>
  active ? "#ffffff" : mainColor || MAIN_COLOR_FALLBACK;

const FormStack = styled.div`
  width: 100%;

  --step-three-main-color: ${({ $mainColor }) =>
    $mainColor || "var(--widget-main-color, #ee754c)"};

  --widget-text-color: ${({ $widgetDark }) =>
    $widgetDark ? "#ffffff" : "#343a38"};

  --widget-muted-text: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.72)" : "#4f5f5b"};

  --widget-border-color: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.24)" : "rgba(153, 176, 173, 0.32)"};

  --widget-soft-bg: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.07)" : "#fafafa"};

  --widget-soft-bg-focus: ${({ $widgetDark }) =>
    $widgetDark ? "rgba(255, 255, 255, 0.1)" : "#ffffff"};

  height: ${({ $widgetMode, $maxHeight }) =>
    $widgetMode ? "100%" : $maxHeight ? `${$maxHeight}px` : "auto"};

  max-height: ${({ $widgetMode, $maxHeight }) =>
    $widgetMode
      ? "100%"
      : $maxHeight
        ? `${$maxHeight}px`
        : "calc(100dvh - 300px)"};

  min-height: 0;

  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  gap: ${({ $widgetMode }) => ($widgetMode ? "8px" : "12px")};

  text-align: left;
  margin-top: ${({ $widgetMode }) => ($widgetMode ? "0" : "8px")};
  padding: ${({ $widgetMode }) =>
    $widgetMode ? "2px 5px 6px" : "2px 8px 10px"};
  box-sizing: border-box;

  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: var(--step-three-main-color, #ee754c) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--step-three-main-color, #ee754c);
    border-radius: 999px;
  }

  @media (max-width: 420px), (max-height: 640px) {
    gap: 7px;
    margin-top: 0;
    padding: 2px 4px 6px;
  }
`;

const FieldBlock = styled.div`
  width: 100%;
  min-width: 0;
`;

const FieldLabel = styled(Form.Label)`
  display: block;
  margin: 0 0 ${({ $widgetMode }) => ($widgetMode ? "3px" : "5px")};
  color: var(--widget-muted-text, #4f5f5b);
  font-size: ${({ $widgetMode }) => ($widgetMode ? "12px" : "14px")};
  font-weight: 650;
  line-height: 1.2;

  @media (max-width: 420px), (max-height: 640px) {
    margin-bottom: 3px;
    font-size: 12px;
  }
`;

const OrangeInput = styled(Form.Control)`
  width: 100%;
  min-width: 0;
  height: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(32px, 8vw, 38px)" : `${FIELD_HEIGHT}px`};
  padding: 0 ${({ $widgetMode }) => ($widgetMode ? "10px" : "14px")};
  font-size: ${({ $widgetMode }) => ($widgetMode ? "13px" : "15px")};
  line-height: 1.35;
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};

  color: var(--widget-text-color, #343a38);
  border-color: var(--widget-border-color, #ced4da);
  background-color: var(--widget-soft-bg, #fafafa);

  box-sizing: border-box;

  &:focus {
    border-color: var(--step-three-main-color, #ee754c) !important;
    box-shadow: 0 0 0 0.18rem rgba(238, 117, 76, 0.18) !important;
    background-color: var(--widget-soft-bg-focus, #ffffff);
    color: var(--widget-text-color, #343a38);
  }

  &.is-invalid {
    border-color: #d94b4b;
  }

  &[readonly] {
    background-color: ${({ $widgetDark }) =>
      $widgetDark ? "rgba(255, 255, 255, 0.05)" : "rgba(153, 176, 173, 0.14)"};
    color: var(--widget-muted-text, #6d7472);
    cursor: not-allowed;
  }

  @media (max-width: 420px), (max-height: 640px) {
    height: 32px;
    padding: 0 9px;
    font-size: 13px;
    border-radius: 8px;
  }
`;

const OrangeTextarea = styled(Form.Control)`
  width: 100%;
  min-width: 0;
  min-height: ${({ $small, $widgetMode }) => {
    if ($widgetMode) return $small ? "58px" : "64px";
    return $small ? "78px" : "88px";
  }};
  padding: ${({ $widgetMode }) => ($widgetMode ? "7px 9px" : "9px 11px")};
  font-size: ${({ $widgetMode }) => ($widgetMode ? "13px" : "15px")};
  line-height: 1.35;
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};
  resize: vertical;

  color: var(--widget-text-color, #343a38);
  border-color: var(--widget-border-color, #ced4da);
  background-color: var(--widget-soft-bg, #fafafa);

  box-sizing: border-box;

  &:focus {
    border-color: var(--step-three-main-color, #ee754c) !important;
    box-shadow: 0 0 0 0.18rem rgba(238, 117, 76, 0.18) !important;
    background-color: var(--widget-soft-bg-focus, #ffffff);
    color: var(--widget-text-color, #343a38);
  }

  @media (max-width: 420px), (max-height: 640px) {
    min-height: ${({ $small }) => ($small ? "52px" : "58px")};
    padding: 7px 9px;
    font-size: 13px;
    border-radius: 8px;
  }
`;

const PhoneFieldFrame = styled.div`
  width: 100%;
  min-width: 0;
  border: ${({ $invalid }) =>
    $invalid ? "1px solid #d94b4b" : "1px solid transparent"};
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};
  padding: ${({ $invalid, $widgetMode }) => {
    if (!$invalid) return "0";
    return $widgetMode ? "4px" : "6px";
  }};
  box-sizing: border-box;

  .react-tel-input {
    width: 100%;
  }

  .react-tel-input .form-control {
    width: 100% !important;
    min-height: ${({ $widgetMode }) =>
      $widgetMode ? "32px" : `${FIELD_HEIGHT}px`} !important;
    height: ${({ $widgetMode }) =>
      $widgetMode ? "32px" : `${FIELD_HEIGHT}px`} !important;
    border-radius: ${({ $widgetMode }) =>
      $widgetMode ? "8px" : "10px"} !important;
    background-color: var(--widget-soft-bg, #fafafa) !important;
    color: var(--widget-text-color, #343a38) !important;
    border-color: var(--widget-border-color, #ced4da) !important;
    font-size: ${({ $widgetMode }) =>
      $widgetMode ? "13px" : "15px"} !important;
    box-sizing: border-box !important;
  }

  .react-tel-input .flag-dropdown {
    border-radius: ${({ $widgetMode }) =>
      $widgetMode ? "8px 0 0 8px" : "10px 0 0 10px"} !important;
    background-color: var(--widget-soft-bg, #fafafa) !important;
    border-color: var(--widget-border-color, #ced4da) !important;
  }

  @media (max-width: 420px), (max-height: 640px) {
    border-radius: 8px;

    .react-tel-input .form-control {
      min-height: 32px !important;
      height: 32px !important;
      font-size: 13px !important;
      border-radius: 8px !important;
    }

    .react-tel-input .flag-dropdown {
      border-radius: 8px 0 0 8px !important;
    }
  }
`;

const ExtrasStack = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ $widgetMode }) => ($widgetMode ? "6px" : "9px")};

  @media (max-width: 420px), (max-height: 640px) {
    gap: 6px;
  }
`;

const ExtrasToggle = styled.button`
  width: 100%;
  min-height: ${({ $widgetMode }) => ($widgetMode ? "32px" : "40px")};
  padding: ${({ $widgetMode }) => ($widgetMode ? "6px 9px" : "8px 12px")};
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};

  border: 1px solid
    ${({ $active, $mainColor }) =>
      $active
        ? getMainColor({ $mainColor })
        : "var(--widget-border-color, #d8dddb)"};

  color: ${({ $active }) =>
    $active ? "#ffffff" : "var(--widget-text-color, #343a38)"};

  background-color: ${({ $active, $mainColor }) =>
    $active ? getMainColor({ $mainColor }) : "transparent"};

  cursor: pointer;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  font-size: ${({ $widgetMode }) => ($widgetMode ? "13px" : "14px")};
  font-weight: 650;
  line-height: 1.2;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${({ $mainColor }) => getMainColor({ $mainColor })};
  }

  @media (max-width: 420px), (max-height: 640px) {
    min-height: 32px;
    padding: 6px 9px;
    border-radius: 8px;
    font-size: 13px;
  }
`;

const ExtrasInside = styled.div`
  width: 100%;
  min-height: ${({ $widgetMode }) => ($widgetMode ? "32px" : "40px")};
  padding: ${({ $widgetMode }) => ($widgetMode ? "6px 9px" : "8px 12px")};
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};

  border: 1px solid
    ${({ $active, $unavailable, $mainColor }) => {
      if ($unavailable) return "#d94b4b";
      if ($active) return getMainColor({ $mainColor });
      return "var(--widget-border-color, #d8dddb)";
    }};

  cursor: ${({ $unavailable }) => ($unavailable ? "not-allowed" : "pointer")};

  color: ${({ $active, $unavailable, $widgetDark }) => {
    if ($unavailable) return $widgetDark ? "#ff8f8f" : "#d94b4b";
    if ($active) return "#ffffff";
    return "var(--widget-text-color, #343a38)";
  }};

  background-color: ${({ $active, $unavailable, $widgetDark, $mainColor }) => {
    if ($unavailable) {
      return $widgetDark
        ? "rgba(217, 75, 75, 0.12)"
        : "rgba(217, 75, 75, 0.05)";
    }

    if ($active) return getMainColor({ $mainColor });

    return "transparent";
  }};

  box-sizing: border-box;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${({ $unavailable, $mainColor }) =>
      $unavailable ? "#d94b4b" : getMainColor({ $mainColor })};
  }

  @media (max-width: 420px), (max-height: 640px) {
    min-height: 32px;
    padding: 6px 9px;
    border-radius: 8px;
  }
`;

const ExtraContent = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $widgetMode }) => ($widgetMode ? "8px" : "12px")};
`;

const ExtraLeft = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};
`;

const ExtraText = styled.span`
  min-width: 0;
  font-size: ${({ $widgetMode }) => ($widgetMode ? "13px" : "14px")};
  font-weight: 650;
  line-height: 1.2;
  white-space: normal;

  @media (max-width: 420px), (max-height: 640px) {
    font-size: 13px;
  }
`;

const ExtraIconSlot = styled.div`
  flex: 0 0 auto;
  width: ${({ $widgetMode }) => ($widgetMode ? "22px" : "26px")};
  min-width: ${({ $widgetMode }) => ($widgetMode ? "22px" : "26px")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DropdownWrap = styled.div`
  width: 100%;
  margin-top: ${({ $widgetMode }) => ($widgetMode ? "0" : "2px")};
`;

const CounterText = styled.div`
  width: 100%;
  margin-top: 3px;
  text-align: right;
  font-size: ${({ $widgetMode }) => ($widgetMode ? "10px" : "12px")};
  color: var(--widget-muted-text, #7d8582);
`;

const getClosestBottomLimit = (element) => {
  if (!element || typeof window === "undefined") return null;

  let bottomLimit = window.innerHeight - 16;

  const modalBody = element.closest(".modal-body");
  if (modalBody) {
    const modalRect = modalBody.getBoundingClientRect();
    if (modalRect.height > 0) {
      bottomLimit = Math.min(bottomLimit, modalRect.bottom - 16);
    }
  }

  let node = element.parentElement;

  while (node && node !== document.body) {
    const styles = window.getComputedStyle(node);
    const rect = node.getBoundingClientRect();

    const clipsContent =
      styles.overflowY === "hidden" ||
      styles.overflowY === "auto" ||
      styles.overflowY === "scroll";

    if (clipsContent && rect.height > 0) {
      bottomLimit = Math.min(bottomLimit, rect.bottom - 16);
      break;
    }

    node = node.parentElement;
  }

  return bottomLimit;
};

function StepThreeReservations({
  fullName,
  setFullName,
  email,
  setEmail,
  emailWS,
  setCountryCodeNumber,
  countryCodeNumber,
  setPhoneWithoutCountryCode,
  phoneWithoutCountryCode,
  isExtrasOpen,
  handleExtras,
  babyChairs,
  setBabyChairs,
  numBabyChairs,
  existingBabyChairs = 0,
  handleBabyChange,
  limitPeopleBaby,
  isMessageOpen,
  setIsMessageOpen,
  isAccessOpen,
  message,
  setMessage,
  maxMessageLength,
  CustomSelect,
  accessabilities,
  setIsAccessOpen,
  setAccessability,
  accessability,
  phoneWS,
  fullNameWS,
  from_restaurant,
  creating,
  restaurantNote,
  setRestaurantNote,
  widgetMode = false,
  widgetDark = false,
  mainColor,
  sub_lvl,
}) {
  const baby_chair_options = limitPeopleBaby?.baby_chair_numbers || [];

  const resolvedMainColor = mainColor || MAIN_COLOR_FALLBACK;
  const currentPlanLevel = Number(sub_lvl);
  const hideReservationExtras =
    currentPlanLevel === 0 || currentPlanLevel === 1;

  const formStackRef = useRef(null);
  const [formMaxHeight, setFormMaxHeight] = useState(null);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  useEffect(() => {
    if (restaurantNote?.length > 0) {
      setIsNoteOpen(true);
    }
  }, [restaurantNote]);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!formStackRef.current) return;

      if (widgetMode) {
        setFormMaxHeight(null);
        return;
      }

      const rect = formStackRef.current.getBoundingClientRect();
      const bottomLimit = getClosestBottomLimit(formStackRef.current);

      if (!bottomLimit || rect.top <= 0) return;

      const bottomReserve = from_restaurant ? 150 : 135;
      const available = Math.floor(bottomLimit - rect.top - bottomReserve);

      setFormMaxHeight(Math.max(150, available));
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);

    if (formStackRef.current?.parentElement) {
      resizeObserver.observe(formStackRef.current.parentElement);
    }

    const modalBody = formStackRef.current?.closest(".modal-body");
    if (modalBody) {
      resizeObserver.observe(modalBody);
    }

    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);

    const raf = requestAnimationFrame(updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      cancelAnimationFrame(raf);
    };
  }, [
    widgetMode,
    from_restaurant,
    creating,
    isExtrasOpen,
    babyChairs,
    isAccessOpen,
    isMessageOpen,
    isNoteOpen,
    limitPeopleBaby,
    hideReservationExtras,
  ]);

  if (!limitPeopleBaby) {
    return null;
  }

  const babyChairsUnavailable = baby_chair_options.length === 0;
  const babyChairActive = babyChairs;

  return (
    <FormStack
      ref={formStackRef}
      $widgetMode={widgetMode}
      $widgetDark={widgetDark}
      $mainColor={mainColor}
      $maxHeight={formMaxHeight}
    >
      <FieldBlock>
        <FieldLabel $widgetMode={widgetMode}>Nome</FieldLabel>
        <OrangeInput
          $widgetMode={widgetMode}
          $widgetDark={widgetDark}
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          onKeyDown={(e) => {
            if (
              ((e.target.value.match(/ /g) || []).length > 1 &&
                e.key === " ") ||
              (e.target.value.length >= 20 &&
                e.key !== "Backspace" &&
                e.key !== "Delete")
            ) {
              e.preventDefault();
            }
          }}
          maxLength={20}
          required
          isInvalid={!!fullNameWS}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel $widgetMode={widgetMode}>E-mail</FieldLabel>
        <OrangeInput
          $widgetMode={widgetMode}
          $widgetDark={widgetDark}
          type="email"
          value={email}
          onChange={(e) => {
            if (!creating && !from_restaurant) return;
            setEmail(e.target.value);
          }}
          readOnly={!creating && !from_restaurant}
          required
          isInvalid={!!emailWS}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldLabel $widgetMode={widgetMode}>Telemóvel</FieldLabel>
        <PhoneFieldFrame $invalid={!!phoneWS} $widgetMode={widgetMode}>
          <PhoneField
            initialFullPhone={`+${countryCodeNumber}${phoneWithoutCountryCode}`}
            countryCodeNumber={countryCodeNumber}
            setCountryCodeNumber={setCountryCodeNumber}
            phoneWithoutCountryCode={phoneWithoutCountryCode}
            setPhoneWithoutCountryCode={setPhoneWithoutCountryCode}
            isPhoneValid={isPhoneValid}
            setIsPhoneValid={setIsPhoneValid}
          />
        </PhoneFieldFrame>
      </FieldBlock>

      {!hideReservationExtras && (
        <ExtrasStack $widgetMode={widgetMode}>
          <ExtrasToggle
            type="button"
            $widgetMode={widgetMode}
            $active={isExtrasOpen}
            $mainColor={resolvedMainColor}
            onClick={handleExtras}
          >
            <span>Extras</span>
            <FontAwesomeIcon
              size={widgetMode ? "sm" : "lg"}
              icon={isExtrasOpen ? faMinus : faPlus}
              style={{
                color: getAccentOrWhite(isExtrasOpen, resolvedMainColor),
              }}
            />
          </ExtrasToggle>

          {isExtrasOpen && (
            <>
              <ExtrasInside
                $widgetMode={widgetMode}
                $widgetDark={widgetDark}
                $mainColor={resolvedMainColor}
                $active={babyChairActive}
                $unavailable={babyChairsUnavailable}
                onClick={() => {
                  if (babyChairsUnavailable) return;

                  const nextValue = !babyChairs;
                  setBabyChairs(nextValue);

                  if (!nextValue) {
                    handleBabyChange(0);
                  } else if (Number(numBabyChairs || 0) <= 0) {
                    handleBabyChange(Number(baby_chair_options[0] || 1));
                  }
                }}
              >
                <ExtraContent $widgetMode={widgetMode}>
                  <ExtraLeft $widgetMode={widgetMode}>
                    <BabyChair
                      width={widgetMode ? 21 : 24}
                      height={widgetMode ? 21 : 24}
                      strokeColor={
                        babyChairsUnavailable
                          ? "#d94b4b"
                          : getAccentOrWhite(babyChairs, resolvedMainColor)
                      }
                    />
                    <ExtraText $widgetMode={widgetMode}>
                      Cadeiras de bebé
                      {babyChairsUnavailable && " (indisponíveis)"}
                    </ExtraText>
                  </ExtraLeft>

                  <ExtraIconSlot $widgetMode={widgetMode}>
                    <FontAwesomeIcon
                      size={widgetMode ? "sm" : "lg"}
                      icon={babyChairs ? faMinus : faPlus}
                      style={{
                        color: getAccentOrWhite(babyChairs, resolvedMainColor),
                      }}
                    />
                  </ExtraIconSlot>
                </ExtraContent>
              </ExtrasInside>

              {babyChairs && (
                <DropdownWrap $widgetMode={widgetMode}>
                  <DropDownBabyChairs
                    babyChairFree={baby_chair_options}
                    existingBabyChairs={existingBabyChairs}
                    fromRestaurant={from_restaurant}
                    initial={numBabyChairs}
                    onChange={handleBabyChange}
                  />
                </DropdownWrap>
              )}

              <ExtrasInside
                $widgetMode={widgetMode}
                $widgetDark={widgetDark}
                $mainColor={resolvedMainColor}
                $active={isAccessOpen}
                onClick={() => setIsAccessOpen(!isAccessOpen)}
              >
                <ExtraContent $widgetMode={widgetMode}>
                  <ExtraLeft $widgetMode={widgetMode}>
                    <FontAwesomeIcon
                      size={widgetMode ? "sm" : "lg"}
                      icon={faWheelchair}
                      style={{
                        color: getAccentOrWhite(
                          isAccessOpen,
                          resolvedMainColor,
                        ),
                      }}
                    />
                    <ExtraText $widgetMode={widgetMode}>
                      Acessibilidade
                    </ExtraText>
                  </ExtraLeft>

                  <ExtraIconSlot $widgetMode={widgetMode}>
                    <FontAwesomeIcon
                      size={widgetMode ? "sm" : "lg"}
                      icon={isAccessOpen ? faMinus : faPlus}
                      style={{
                        color: getAccentOrWhite(
                          isAccessOpen,
                          resolvedMainColor,
                        ),
                      }}
                    />
                  </ExtraIconSlot>
                </ExtraContent>
              </ExtrasInside>

              {isAccessOpen && (
                <DropdownWrap $widgetMode={widgetMode}>
                  <CustomSelect
                    items={accessabilities}
                    onSelectChange={(value) => setAccessability(value)}
                    selectedItem={accessability}
                    setItem={setAccessability}
                    widgetDark={widgetDark}
                    mainColor={resolvedMainColor}
                  />
                </DropdownWrap>
              )}

              {creating && (
                <>
                  <ExtrasInside
                    $widgetMode={widgetMode}
                    $widgetDark={widgetDark}
                    $mainColor={resolvedMainColor}
                    $active={isMessageOpen}
                    onClick={() => setIsMessageOpen(!isMessageOpen)}
                  >
                    <ExtraContent $widgetMode={widgetMode}>
                      <ExtraLeft $widgetMode={widgetMode}>
                        <FontAwesomeIcon
                          size={widgetMode ? "sm" : "lg"}
                          icon={faMessage}
                          style={{
                            color: getAccentOrWhite(
                              isMessageOpen,
                              resolvedMainColor,
                            ),
                          }}
                        />
                        <ExtraText $widgetMode={widgetMode}>Mensagem</ExtraText>
                      </ExtraLeft>

                      <ExtraIconSlot $widgetMode={widgetMode}>
                        <FontAwesomeIcon
                          size={widgetMode ? "sm" : "lg"}
                          icon={isMessageOpen ? faMinus : faPlus}
                          style={{
                            color: getAccentOrWhite(
                              isMessageOpen,
                              resolvedMainColor,
                            ),
                          }}
                        />
                      </ExtraIconSlot>
                    </ExtraContent>
                  </ExtrasInside>

                  {isMessageOpen && (
                    <DropdownWrap $widgetMode={widgetMode}>
                      <OrangeTextarea
                        $widgetMode={widgetMode}
                        $widgetDark={widgetDark}
                        as="textarea"
                        rows={3}
                        value={message}
                        maxLength={maxMessageLength}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                      />
                      <CounterText $widgetMode={widgetMode}>
                        {message.length}/{maxMessageLength}
                      </CounterText>
                    </DropdownWrap>
                  )}
                </>
              )}
            </>
          )}
        </ExtrasStack>
      )}

      {from_restaurant && (
        <ExtrasStack $widgetMode={widgetMode}>
          <ExtrasInside
            $widgetMode={widgetMode}
            $widgetDark={widgetDark}
            $mainColor={resolvedMainColor}
            $active={isNoteOpen}
            onClick={() => setIsNoteOpen(!isNoteOpen)}
          >
            <ExtraContent $widgetMode={widgetMode}>
              <ExtraLeft $widgetMode={widgetMode}>
                <FontAwesomeIcon
                  size={widgetMode ? "sm" : "lg"}
                  icon={faNoteSticky}
                  style={{
                    color: getAccentOrWhite(isNoteOpen, resolvedMainColor),
                  }}
                />
                <ExtraText $widgetMode={widgetMode}>Nota interna</ExtraText>
              </ExtraLeft>

              <ExtraIconSlot $widgetMode={widgetMode}>
                <FontAwesomeIcon
                  size={widgetMode ? "sm" : "lg"}
                  icon={isNoteOpen ? faMinus : faPlus}
                  style={{
                    color: getAccentOrWhite(isNoteOpen, resolvedMainColor),
                  }}
                />
              </ExtraIconSlot>
            </ExtraContent>
          </ExtrasInside>

          {isNoteOpen && (
            <DropdownWrap $widgetMode={widgetMode}>
              <OrangeTextarea
                $widgetMode={widgetMode}
                $widgetDark={widgetDark}
                as="textarea"
                rows={3}
                value={restaurantNote}
                maxLength={maxMessageLength}
                onChange={(e) => setRestaurantNote(e.target.value)}
                required
                $small
              />
              <CounterText $widgetMode={widgetMode}>
                {restaurantNote.length}/{maxMessageLength}
              </CounterText>
            </DropdownWrap>
          )}
        </ExtrasStack>
      )}
    </FormStack>
  );
}

export default StepThreeReservations;
